import { ChangeEvent, memo, useCallback } from 'react'

import { Box, ButtonBase, InputBase, Paper, styled, Typography } from '@mui/material'
import { VideosGetRequest } from '@nativewaves/nw-cloud-sdk/content'
import { useNavigate } from '@tanstack/react-router'
import { format, setHours, setMinutes } from 'date-fns'
import { omit, pickBy } from 'lodash-es'

import { DatePicker } from '@shared/components/DateTimePicker'
import { FoldoutPanel, useCloseHandler } from '@shared/components/Foldout'
import { Kbd } from '@shared/components/MaterialUIEnhancements'
import { videoObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects'

const parseNewDate = (time: string, baseDate: Date) => {
  const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10))
  return setHours(setMinutes(baseDate, minutes), hours)
}

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(min-content, 1fr)',
  gridAutoRows: 'minmax(32px, auto)',
  alignItems: 'center',
  justifyContent: 'stretch',
  columnGap: theme.spacing(4),
  rowGap: theme.spacing(3),
}))

const TimePicker = styled(InputBase)(({ theme }) => ({
  ...theme.typography['heading-base'],
  lineHeight: 1,
  borderRadius: 1,
  backgroundColor: theme.palette.background.levels[500],
  padding: theme.spacing(1),
  margin: theme.spacing(-1),

  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    fontSize: 24,
    marginRight: theme.spacing(0.5),
    cursor: 'pointer',
  },
}))

const FilterPanelRaw = () => {
  const handleClose = useCloseHandler()

  const navigate = useNavigate({ from: videoObjectsOverviewRoute.fullPath })
  const search = videoObjectsOverviewRoute.useSearch({
    select: (items) =>
      pickBy(items, (_, key) => key.startsWith('timeRange')) as PickStartsWith<VideosGetRequest, 'timeRange'>,
    structuralSharing: true,
  })

  const handleDateRangeChange = useCallback(
    (prop: keyof PickStartsWith<VideosGetRequest, 'timeRange'>) => (date: Date | null) => {
      if (date) {
        navigate({
          to: videoObjectsOverviewRoute.to,
          search: (current) => ({ ...current, [prop]: date.valueOf() / 1000 }),
        })
      } else {
        navigate({ to: videoObjectsOverviewRoute.to, search: (current) => omit(current, prop) })
      }
    },
    [navigate],
  )

  const handleTimeRangeChange = useCallback(
    (prop: keyof PickStartsWith<VideosGetRequest, 'timeRange'>) => (e: ChangeEvent<HTMLInputElement>) => {
      const time = e.target.value
      const searchValue = search[prop]

      if (searchValue) {
        navigate({
          to: videoObjectsOverviewRoute.to,
          search: (current) => ({
            ...current,
            [prop]: parseNewDate(time, new Date(searchValue * 1000)).valueOf() / 1000,
          }),
        })
      }
    },
    [navigate, search],
  )

  return (
    <FoldoutPanel
      title={<Typography variant="heading-lg">Filter Options</Typography>}
      breadcrumbs={['Events']}
      actions={
        <ButtonBase onClick={() => handleClose({}, 'escapeKeyDown')}>
          <Kbd sx={{ py: 0 }}>Esc</Kbd>
        </ButtonBase>
      }
    >
      <ContentWrapper>
        <Paper sx={{ m: -1, p: 1, gridColumn: '1 / span 2', bgcolor: 'background.levels.100' }}>
          <Typography variant="heading-md">Time Ranges</Typography>
        </Paper>

        <Typography variant="text">From:</Typography>
        <DatePicker
          sx={{ borderRadius: 1, bgcolor: 'background.levels.500', p: 1, m: -1 }}
          defaultDate={search.timeRangeStart ? new Date(search.timeRangeStart * 1000) : undefined}
          onChange={handleDateRangeChange('timeRangeStart')}
        />

        {search.timeRangeStart && (
          <TimePicker
            sx={{ gridColumn: 2 }}
            type="time"
            defaultValue={search.timeRangeStart ? format(new Date(search.timeRangeStart * 1000), 'HH:mm') : '00:00'}
            onChange={handleTimeRangeChange('timeRangeStart')}
          />
        )}

        <Typography variant="text">Until:</Typography>
        <DatePicker
          sx={{ borderRadius: 1, bgcolor: 'background.levels.500', p: 1, m: -1 }}
          onChange={handleDateRangeChange('timeRangeEnd')}
          defaultDate={search.timeRangeEnd ? new Date(search.timeRangeEnd * 1000) : undefined}
        />
        {search.timeRangeEnd && (
          <TimePicker
            sx={{ gridColumn: 2 }}
            type="time"
            defaultValue={search.timeRangeEnd ? format(new Date(search.timeRangeEnd * 1000), 'HH:mm') : '00:00'}
            onChange={handleTimeRangeChange('timeRangeEnd')}
          />
        )}
      </ContentWrapper>
    </FoldoutPanel>
  )
}

export const FilterPanel = memo(FilterPanelRaw)
